import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Container, Typography } from '@mui/material';
import { RefreshOutlined as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component to catch and handle React errors gracefully
 * Prevents entire app from crashing when a component error occurs
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Fehler an externes Monitoring (Sentry / Application Insights) senden
    // Beispiel: logErrorToService(error, errorInfo);
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  private handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              py: 4,
            }}
          >
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              <AlertTitle>Ein Fehler ist aufgetreten</AlertTitle>
              Es tut uns leid, aber etwas ist schiefgelaufen. Die Anwendung konnte nicht korrekt geladen werden.
            </Alert>

            {!import.meta.env.PROD && this.state.error && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2" color="error" gutterBottom>
                  <strong>Fehlerdetails:</strong>
                </Typography>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    backgroundColor: 'action.hover',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo && (
                    <>
                      {'\n\n'}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </Typography>
              </Box>
            )}

            {import.meta.env.PROD && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bitte laden Sie die Seite neu oder kontaktieren Sie den Support, falls das Problem weiterhin besteht.
              </Typography>
            )}

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReset}
              size="large"
            >
              Seite neu laden
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Wenn das Problem weiterhin besteht, wenden Sie sich bitte an den Support.
            </Typography>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
